import React, { useEffect } from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  useDisclosure,
  Stack,
  Text,
  Link,
  useColorModeValue,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Divider,
} from "@chakra-ui/react";
import {
  HamburgerIcon,
  CloseIcon,
  BellIcon,
  MoonIcon,
  SunIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";
import { HiOutlineShoppingCart } from "react-icons/hi";
import { useShopping } from "../context/ShoppingContext";

const MotionLink = motion(Link);

const Links = [
  { label: "Dashboard", href: "/", disabled: false },
  { label: "Attendance", href: "/attendance", disabled: true },
  { label: "Reports", href: "/reports", disabled: true },
  { label: "Settings", href: "/settings", disabled: false },
];

const NavLink = ({ label, href, disabled, onNavigate }) => {
  const location = useLocation(); // FIX: get location inside hook
  const inactiveColor = useColorModeValue("gray.600", "gray.300");
  const isActive = location.pathname === href;

  return (
    <MotionLink
      whileHover={!disabled ? { scale: 1.05 } : {}}
      transition={{ duration: 0.2 }}
      px={4}
      py={2}
      rounded="full"
      fontWeight="medium"
      fontSize="sm"
      bg={isActive ? "teal.100" : "transparent"}
      color={
        disabled
          ? "gray.400"
          : isActive
          ? "teal.700"
          : inactiveColor
      }
      _hover={
        disabled
          ? {}
          : {
              textDecoration: "none",
              bg: "teal.200",
              transition: "all 0.2s ease-in-out",
            }
      }
      href={disabled ? undefined : href}
      pointerEvents={disabled ? "none" : "auto"}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      opacity={disabled ? 0.6 : 1}
      cursor={disabled ? "not-allowed" : "pointer"}
      onClick={onNavigate}
    >
      {label}
    </MotionLink>
  );
};

const StickyNavbar = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const logoSrc = useColorModeValue("/image.png", "/image_dark_mode.png");
  const { cart } = useShopping();
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const [isCartOpen, setCartOpen] = React.useState(false);

  useEffect(() => {
    onClose(); // Auto-close mobile nav when route changes
  }, [location.pathname]);

  const handleLogout = () => {
    navigate("/login");
  };

  const handleMyProfile = () => {
    navigate("/MyProfile");
  };

  return (
    <Box
      position="sticky"
      top="0"
      zIndex="999"
      bg={useColorModeValue("whiteAlpha.800", "gray.800")}
      px={4}
      backdropFilter="saturate(180%) blur(12px)"
      boxShadow="sm"
      borderBottom="1px solid"
      borderColor={useColorModeValue("gray.100", "gray.700")}
    >
      <Flex
        h={16}
        alignItems="center"
        justifyContent="space-between"
        overflow={"hidden"}
      >
        {/* Logo */}
        <Flex align="center" gap={3} overflow={"hidden"}>
          <Image
            src={logoSrc}
            alt="Novatra - “Bright. Seamless. Secure.”"
            boxSize="150"
          />
          <Text
            fontWeight="bold"
            fontSize="lg"
            bgGradient="linear(to-r, teal.400, blue.500)"
            bgClip="text"
          >
            {/* Novatra - “Bright. Seamless. Secure.” */}
          </Text>
        </Flex>

        {/* Desktop Nav */}
        <HStack spacing={2} display={{ base: "none", md: "flex" }}>
          {Links.map((link) => (
            <NavLink key={link.label} {...link} />
          ))}
        </HStack>

        {/* Action Icons Grouped for Consistent Placement */}
        <HStack spacing={{ base: 1, md: 3 }} align="center">
          {/* Dark/Light Toggle */}
          <IconButton
            icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
            onClick={toggleColorMode}
            variant="ghost"
            aria-label="Toggle theme"
          />
          {/* Notification Bell */}
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<BellIcon />}
              variant="ghost"
              aria-label="Notifications"
              size="md"
            />
            <MenuList minW="260px">
              <MenuItem isDisabled fontWeight="bold" color="gray.600">
                Notifications
              </MenuItem>
              <MenuItem>No new notifications</MenuItem>
              {/* Add more MenuItem for actual notifications here */}
            </MenuList>
          </Menu>
          {/* Profile Menu */}
          <Menu>
            <MenuButton>
              <Avatar size="sm" name="Abhishek Advani" src="/profile.png" />
            </MenuButton>
            <MenuList>
              <MenuItem onClick={handleMyProfile}>My Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </MenuList>
          </Menu>
          {/* Cart Icon - always far right */}
          <Box
            position="relative"
            ml={{ base: 2, md: 4 }}
            display="flex"
            alignItems="center"
          >
            <IconButton
              icon={<HiOutlineShoppingCart size={26} />}
              aria-label="Cart"
              colorScheme="teal"
              variant="ghost"
              size="lg"
              onClick={() => setCartOpen(true)}
              _hover={{ bg: "teal.50", boxShadow: "md" }}
              borderRadius="full"
              p={2}
            />
            {cartCount > 0 && (
              <Box
                position="absolute"
                top={1}
                right={1}
                bg="red.500"
                color="white"
                borderRadius="full"
                px={2}
                fontSize="xs"
                fontWeight="bold"
                minW={5}
                textAlign="center"
                zIndex={1}
                boxShadow="sm"
              >
                {cartCount}
              </Box>
            )}
          </Box>
          {/* Hamburger for mobile */}
          <IconButton
            size="md"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label="Toggle Navigation"
            display={{ md: "none" }}
            onClick={isOpen ? onClose : onOpen}
            variant="ghost"
          />
        </HStack>
      </Flex>
      {/* Cart Drawer - must be outside Flex/HStack for reliable open/close */}
      <Drawer
        isOpen={isCartOpen}
        placement="right"
        onClose={() => setCartOpen(false)}
        size="sm"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader
            borderBottomWidth="1px"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box fontWeight="bold">Your Cart</Box>
            <IconButton
              icon={<CloseIcon />}
              aria-label="Close cart"
              size="sm"
              variant="ghost"
              onClick={() => setCartOpen(false)}
            />
          </DrawerHeader>
          <DrawerBody>
            {cart.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                h="100%"
                py={8}
              >
                <Text fontSize="lg" color="gray.500" mb={2}>
                  Your cart is empty.
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Add products to see them here.
                </Text>
              </Flex>
            ) : (
              <Stack spacing={5}>
                {cart.map((item) => (
                  <Flex
                    key={item.id}
                    align="center"
                    justify="space-between"
                    p={2}
                    borderRadius="md"
                    bg={useColorModeValue("gray.50", "gray.700")}
                    boxShadow="sm"
                  >
                    <Flex align="center" gap={3} flex={1}>
                      <Image
                        src={item.image}
                        alt={item.name}
                        boxSize="80px"
                        borderRadius="md"
                      />
                      <Box>
                        <Text fontWeight="bold" fontSize="md">
                          {item.name}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {item.series}
                        </Text>
                        <Text fontSize="sm" color="blue.600">
                          {item.price}
                        </Text>
                        <Flex align="center" gap={2} mt={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() =>
                              updateCartQuantity(item.id, item.quantity - 1)
                            }
                            isDisabled={item.quantity <= 1}
                          >
                            -
                          </Button>
                          <Text fontSize="sm" minW={6} textAlign="center">
                            {item.quantity}
                          </Text>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() =>
                              updateCartQuantity(item.id, item.quantity + 1)
                            }
                          >
                            +
                          </Button>
                        </Flex>
                      </Box>
                    </Flex>
                    <IconButton
                      icon={<DeleteIcon />}
                      aria-label="Remove item"
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => removeFromCart(item.id)}
                    />
                  </Flex>
                ))}
                <Divider />
                <Flex
                  align="center"
                  justify="space-between"
                  fontWeight="bold"
                  fontSize="lg"
                  py={2}
                >
                  <Text>Subtotal</Text>
                  <Text color="teal.600">
                    ₹
                    {cart
                      .reduce((total, item) => {
                        const rawPrice = item.price ?? item.mrp ?? item.baseSellingPrice ?? 0;
                        const price = typeof rawPrice === "number"
                          ? rawPrice
                          : parseFloat(String(rawPrice).replace(/₹/g, "").replace(/,/g, "")) || 0;
                        return total + price * item.quantity;
                      }, 0)
                      .toLocaleString()}
                  </Text>
                </Flex>
              </Stack>
            )}
          </DrawerBody>
          <Divider />
          <DrawerFooter display="flex" flexDirection="column" gap={3}>
            <Button
              colorScheme="orange"
              w="full"
              onClick={() => {
                setCartOpen(false);
                navigate("/checkout");
              }}
              isDisabled={cart.length === 0}
              mb={2}
            >
              Checkout
            </Button>
            <Button
              w="full"
              variant="outline"
              onClick={() => setCartOpen(false)}
            >
              Cancel
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Mobile Nav */}
      {isOpen && (
        <Box pb={4} display={{ md: "none" }}>
          <Stack as="nav" spacing={2}>
            {Links.map((link) => (
              <NavLink key={link.label} {...link} onNavigate={onClose} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default StickyNavbar;
